import { Form } from 'antd';
import { useParams } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { getUpdatedPayload } from 'utils/helpers';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { useCountryByIdQuery, useCountryWarehouseCreateMutation, useCountryWarehouseUpdateMutation } from 'api';

interface IFormData {
  name: string;
  streetAddress: string;
  zip: string;
  city: string;
  countryId?: string;
  id?: string;
}

interface IUseAddEdit {
  dataToEdit?: IFormData | undefined;
  onModalStateChange: () => void;
}

export const useAddEdit = (props: IUseAddEdit) => {
  const { dataToEdit, onModalStateChange } = props;
  const { id } = useParams() as { id: string };
  const [form] = Form.useForm();
  const { handleError, handleSuccess } = useNotificationMessages();
  const { data: countryData = [] } = useCountryByIdQuery(
    { countryId: id },
    {
      skip: !id,
    }
  );
  const [createCountryWarehouse, { isLoading: createIsLoading }] = useCountryWarehouseCreateMutation();
  const [updateCountryWarehouse, { isLoading: updateIsLoading }] = useCountryWarehouseUpdateMutation();

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const handleSubmit = useCallback(async () => {
    const formFields: IFormData = form.getFieldsValue();
    const payload = getUpdatedPayload(formFields, dataToEdit);
    if (dataToEdit) {
      payload.id = dataToEdit.id;
    } else {
      payload.countryId = id;
    }
    try {
      const { data, error }: any = await (dataToEdit ? updateCountryWarehouse : createCountryWarehouse)(payload);
      if (data?.success) {
        handleSuccess(`Warehouse was successfully ${dataToEdit ? 'updated' : 'created'}.`);
        onCancel();
      } else {
        handleError(error);
      }
    } catch (err) {
      handleError(err);
    }
  }, [dataToEdit, createCountryWarehouse, updateCountryWarehouse, form, id, handleError, handleSuccess, onCancel]);

  useEffect(() => {
    if (dataToEdit) {
      form.setFieldsValue(dataToEdit);
    }
  }, [dataToEdit, form]);

  return {
    form,
    handleSubmit,
    isLoadingUpdate: updateIsLoading || createIsLoading,
    onCancel,
    states: countryData.states || [],
  };
};
